import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userApi, roleApi, menuApi, orderApi, salesApi, tableApi } from '../services/api';
import type { User as UserType, Role as RoleType, Category as CategoryType, MenuItem as MenuItemType, Order as OrderType } from '../types';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { fetchTables, createTable, deleteTable } from '../store/slices/tablesSlice';
import { api } from '../services/api';
import config from '../config';

const roleTitles: Record<string, string> = {
  admin: 'Admin Dashboard',
  chef: 'Kitchen Dashboard',
  server: 'Server Dashboard',
  manager: 'Manager Dashboard'
};

const roleSubtitles: Record<string, string> = {
  admin: 'System Overview and Control',
  chef: 'Orders in Preparation',
  server: 'Service Management Panel',
  manager: 'Reports and Team Activity',
};

// Helper function for date formatting
const formatDate = (date: Date, format: string): string => {
  const pad = (num: number): string => num.toString().padStart(2, '0');
  
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  const seconds = pad(date.getSeconds());
  
  return format
    .replace('yyyy', year.toString())
    .replace('MM', month)
    .replace('dd', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds);
};

// Mock data
const mockUsers = [
  { id: 1, username: 'admin', role: 'admin', lastLogin: '2024-03-20 10:30:00' },
  { id: 2, username: 'server1', role: 'server', lastLogin: '2024-03-20 09:15:00' },
  { id: 3, username: 'chef1', role: 'chef', lastLogin: '2024-03-20 08:45:00' },
];

const mockSales = {
  total: 1500,
  today: 300,
  recent: [
    { id: 1, amount: 50, date: '2023-10-01' },
    { id: 2, amount: 75, date: '2023-10-02' },
  ],
};

const mockTables = [
  { id: 1, number: 1, status: 'available' },
  { id: 2, number: 2, status: 'occupied' },
  { id: 3, number: 3, status: 'reserved' },
];

const mockMenu = [
  { id: 1, name: 'Pizza', price: 10, category: 'Main' },
  { id: 2, name: 'Salad', price: 5, category: 'Appetizer' },
];

const mockActivity = [
  { id: 1, type: 'order', description: 'New order #1 placed at Table 1', time: '10:30 AM' },
  { id: 2, type: 'payment', description: 'Payment received for order #2', time: '10:45 AM' },
  { id: 3, type: 'table', description: 'Table 3 reserved for 2:00 PM', time: '11:00 AM' },
];

// Mock activity log
const mockActivityLog = [
  { id: 1, userId: 2, action: 'login', timestamp: '2024-03-20 10:30:00' },
  { id: 2, userId: 2, action: 'create_order', timestamp: '2024-03-20 10:35:00' },
  { id: 3, userId: 3, action: 'update_order', timestamp: '2024-03-20 10:40:00' },
];

// Add mock statistics data
const mockStatistics = [
  {
    username: 'admin',
    role: 'admin',
    active_days: 30,
    total_logins: 45,
    total_orders_created: 0,
    total_orders_modified: 0
  },
  {
    username: 'server1',
    role: 'server',
    active_days: 25,
    total_logins: 38,
    total_orders_created: 156,
    total_orders_modified: 23
  },
  {
    username: 'chef1',
    role: 'chef',
    active_days: 28,
    total_logins: 42,
    total_orders_created: 0,
    total_orders_modified: 189
  }
];

// Define types for our data
interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  category_id: number;
  image_url?: string;
  is_available: boolean;
}

interface Category {
  id: number;
  name: string;
  description: string;
}

interface Order {
  id: number;
  table_number: number;
  status: 'pending' | 'preparing' | 'ready' | 'served' | 'cancelled';
  total_amount: number;
  created_at: string;
  created_by: number;
  created_by_name: string;
  updated_at?: string;
}

interface NewMenuItem {
  name: string;
  description: string;
  price: number;
  category_id: number;
  image_url?: string;
  is_available?: boolean;
}

// Add mock data with proper types
const mockData = {
  categories: [
    { id: 1, name: 'Appetizers', description: 'Starters and small plates', created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' },
    { id: 2, name: 'Main Courses', description: 'Main dishes', created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' },
    { id: 3, name: 'Desserts', description: 'Sweet treats', created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' }
  ],
  menuItems: [
    { id: 1, name: 'Caesar Salad', description: 'Fresh romaine lettuce with Caesar dressing', price: 8.99, category_id: 1, category_name: 'Appetizers', image_url: '/images/caesar-salad.jpg', is_available: true, created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' },
    { id: 2, name: 'Steak', description: 'Grilled beef steak with vegetables', price: 24.99, category_id: 2, category_name: 'Main Courses', image_url: '/images/steak.jpg', is_available: true, created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' },
    { id: 3, name: 'Chocolate Cake', description: 'Rich chocolate cake with ganache', price: 6.99, category_id: 3, category_name: 'Desserts', image_url: '/images/chocolate-cake.jpg', is_available: true, created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' }
  ],
  orders: [
    { 
      id: 1, 
      table_number: 1, 
      created_by: 1, 
      created_by_name: 'John Doe', 
      status: 'pending' as const, 
      total_amount: 33.97, 
      items: [
        { id: 1, order_id: 1, menu_item_id: 1, menu_item_name: 'Caesar Salad', quantity: 1, price_at_time: 8.99, created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' },
        { id: 2, order_id: 1, menu_item_id: 2, menu_item_name: 'Steak', quantity: 1, price_at_time: 24.99, created_at: '2024-03-20T00:00:00Z', updated_at: '2024-03-20T00:00:00Z' }
      ], 
      created_at: '2024-03-20T00:00:00Z', 
      updated_at: '2024-03-20T00:00:00Z' 
    }
  ] as OrderType[]
};

// Add Role type
interface Role {
  id: number;
  name: string;
  description: string;
}

// Update User interface
interface User {
  id: number;
  username: string;
  role: string;
  roleDescription?: string;
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  archived?: boolean;
}

interface NewOrder {
  table_number: number;
  items: Array<{
    menu_item_id: number;
    quantity: number;
    notes?: string;
  }>;
}

interface Sale {
  id: number;
  amount: number;
  date: string;
}

interface SalesData {
  total: number;
  today: number;
  recent: Sale[];
}

interface Table {
  id: number;
  number: number;
  status: string;
}

// Add these interfaces at the top of the file
interface TableColumn {
  header: string;
  key: string;
  render?: (item: any) => React.ReactNode;
}

// Move components outside of Dashboard
const TableView: React.FC<{
  data: any[];
  columns: TableColumn[];
  isMobile: boolean;
}> = ({ data, columns, isMobile }) => {
  if (isMobile) {
    return (
      <div className="space-y-4">
        {data.map((item, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-4 space-y-3">
            {columns.map((column, colIndex) => (
              <div key={colIndex} className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-500">{column.header}</span>
                <span className="text-sm text-gray-900">{column.render ? column.render(item) : item[column.key]}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column, index) => (
              <th
                key={index}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((item, index) => (
            <tr key={index}>
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                >
                  {column.render ? column.render(item) : item[column.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Modal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{title}</h3>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ActionButton: React.FC<{
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  className?: string;
  children: React.ReactNode;
  disabled?: boolean;
}> = ({ onClick, variant = 'primary', className = '', children, disabled = false }) => {
  const baseClasses = "px-4 py-2 rounded-md text-white font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 min-h-[44px] min-w-[44px] flex items-center justify-center";
  const variantClasses = {
    primary: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    secondary: "bg-gray-600 hover:bg-gray-700 focus:ring-gray-500",
    danger: "bg-red-600 hover:bg-red-700 focus:ring-red-500"
  };

  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "";

  return (
    <button
      onClick={onClick}
      className={`${baseClasses} ${variantClasses[variant]} ${disabledClasses} ${className}`}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

// Custom hook for mobile detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

interface Toast {
    id: number;
    message: string;
    type: 'success' | 'error';
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [userRole, setUserRole] = useState<string>('');
  const [activeTab, setActiveTab] = useState('orders');
  const [showCreateOrder, setShowCreateOrder] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditRole, setShowEditRole] = useState<number | null>(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    pin: '',
    role: ''
  });
  const [newRole, setNewRole] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [editPin, setEditPin] = useState('');
  const [showEditPin, setShowEditPin] = useState<number | null>(null);
  const [showStatistics, setShowStatistics] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: formatDate(new Date(new Date().setDate(new Date().getDate() - 30)), 'yyyy-MM-dd'),
    end: formatDate(new Date(), 'yyyy-MM-dd')
  });
  const [statistics, setStatistics] = useState(mockStatistics);

  // Add new state variables for menu management
  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [menuItems, setMenuItems] = useState<MenuItemType[]>([]);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [showCreateMenuItem, setShowCreateMenuItem] = useState(false);
  const [showEditMenuItem, setShowEditMenuItem] = useState<number | null>(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [newMenuItem, setNewMenuItem] = useState<NewMenuItem>({
    name: '',
    description: '',
    price: 0,
    category_id: 0,
    image_url: '',
    is_available: true
  });

  // Add new state variables for order management
  const [orders, setOrders] = useState<OrderType[]>([]);
  const [selectedTableOrders, setSelectedTableOrders] = useState<OrderType[]>([]);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [showActivityLog, setShowActivityLog] = useState<number | null>(null);

  // Add new state variables for loading
  const [isLoading, setIsLoading] = useState({
    users: false,
    roles: false,
    categories: false,
    menuItems: false,
    orders: false,
    sales: false,
    tables: false
  });

  const [newOrder, setNewOrder] = useState<NewOrder>({
    table_number: 0,
    items: []
  });

  // Add new state variables for sales
  const [sales, setSales] = useState<SalesData>({
    total: 0,
    today: 0,
    recent: []
  });

  // Add back the table management state
  const [showCreateTable, setShowCreateTable] = useState(false);
  const [newTableNumber, setNewTableNumber] = useState('');
  const [showDeleteTableConfirm, setShowDeleteTableConfirm] = useState(false);
  const [tableToDelete, setTableToDelete] = useState<number | null>(null);

  const [showArchived, setShowArchived] = useState(false);

  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUserForRoleChange, setSelectedUserForRoleChange] = useState<number | null>(null);

  const [selectedOrder, setSelectedOrder] = useState<OrderType | null>(null);

  // Get tables state from Redux
  const { items: tables, loading: tablesLoading, error: tablesError } = useSelector((state: RootState) => state.tables);
  const dispatch = useDispatch<AppDispatch>();

  // Update fetchTables function
  const fetchTablesData = () => {
    dispatch(fetchTables());
  };

  // Update handleCreateTable function
  const handleCreateTable = async () => {
    try {
      const tableNumber = parseInt(newTableNumber);
      if (tableNumber < 1) {
        setError('Please enter a valid table number');
        return;
      }

      await dispatch(createTable(tableNumber));
      setShowCreateTable(false);
      setNewTableNumber('');
    } catch (error) {
      console.error('Error creating table:', error);
      if (axios.isAxiosError(error)) {
        setError(error.message || 'Failed to create table');
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to create table');
      }
    }
  };

  // Update handleDeleteTable function
  const handleDeleteTable = async (tableId: number) => {
    if (window.confirm('Are you sure you want to delete this table?')) {
      try {
        await dispatch(deleteTable(tableId));
      } catch (error) {
        console.error('Error deleting table:', error);
        if (axios.isAxiosError(error)) {
          setError(error.message || 'Failed to delete table');
        } else if (error instanceof Error) {
          setError(error.message);
        } else {
          setError('Failed to delete table');
        }
      }
    }
  };

  // Update useEffect for tables
  useEffect(() => {
    if (activeTab === 'tables') {
      fetchTablesData();
    }
  }, [activeTab]);

  // Define fetchOrders before useEffects
  const fetchOrders = async () => {
    setIsLoading(prev => ({ ...prev, orders: true }));
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Failed to fetch orders');
    } finally {
      setIsLoading(prev => ({ ...prev, orders: false }));
    }
  };

  useEffect(() => {
    // Get user role from localStorage or API
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserRole(payload.role);
        // Set default tab to orders for all users
        setActiveTab('orders');
        // Fetch initial data
        fetchData();
      } catch (error) {
        console.error('Error parsing token:', error);
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const fetchData = async () => {
    try {
      // Fetch all data in parallel
      const [users, roles, categories, menuItems, orders] = await Promise.all([
        userApi.getUsers(),
        roleApi.getRoles(),
        menuApi.getCategories(),
        menuApi.getMenuItems(),
        orderApi.getOrders()
      ]);

      // Update state with fresh data from backend
      setUsers(users);
      setRoles(roles);
      setCategories(categories);
      setMenuItems(menuItems);
      setOrders(orders);

      // Log the users for debugging
      console.log('Fetched users:', users);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to fetch data. Please try again.');
    }
  };

  // Filter users based on search query and filter
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === 'all' || user.role === filter;
    return matchesSearch && matchesFilter;
  });

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (!bulkAction || selectedUsers.length === 0) {
      setError('Please select an action and at least one user');
      return;
    }

    try {
      switch (bulkAction) {
        case 'delete':
          if (!window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
            return;
          }
          await Promise.all(selectedUsers.map(userId => api.delete(`/users/${userId}`)));
          break;
        case 'activate':
          await Promise.all(selectedUsers.map(userId => 
            api.patch(`/users/${userId}/status`, { status: 'active' })
          ));
          break;
        case 'deactivate':
          await Promise.all(selectedUsers.map(userId => 
            api.patch(`/users/${userId}/status`, { status: 'inactive' })
          ));
          break;
        default:
          setError('Invalid action');
          return;
      }

      // Refresh users list
      const response = await api.get('/users');
      setUsers(response.data);
      setSelectedUsers([]);
      setShowBulkActions(false);
      setBulkAction('');
      addToast(`Successfully ${bulkAction}ed ${selectedUsers.length} users`, 'success');
    } catch (error) {
      console.error('Error performing bulk action:', error);
      addToast('Failed to perform bulk action', 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(e.target.value);
  };

  const handleCreateUser = async (userData: { username: string; pin: string; role: string }) => {
    try {
        await api.post('/users', userData);
        const response = await api.get('/users');
        setUsers(response.data);
        handleSuccess('User created successfully');
        setShowCreateUser(false);
    } catch (error) {
        console.error('Error creating user:', error);
        handleError('Failed to create user');
    }
};

  const handleUpdateRole = async (userId: number) => {
    try {
        await api.put(`/users/${userId}/role`, { role: newRole });
        const response = await api.get('/users');
        setUsers(response.data);
        handleSuccess('User role updated successfully');
      setShowRoleModal(false);
      setSelectedUserForRoleChange(null);
      setNewRole('');
    } catch (error) {
        console.error('Error updating user role:', error);
        handleError('Failed to update user role');
    }
};

  const handleResetPin = async (userId: number) => {
    try {
        const newPin = window.prompt('Enter new PIN:');
        if (!newPin) return;

        await api.put(`/users/${userId}/pin`, { pin: newPin });
        handleSuccess('User PIN reset successfully');
    } catch (error) {
        console.error('Error resetting user PIN:', error);
        handleError('Failed to reset user PIN');
    }
};

  const handleCreateOrder = async (orderData: any) => {
    try {
        await api.post('/orders', orderData);
        handleSuccess('Order created successfully');
        setShowCreateOrder(false);
        fetchData();
    } catch (error) {
        console.error('Error creating order:', error);
        handleError('Failed to create order');
    }
};

  const handleUpdateOrderStatus = async (orderId: number, status: Order['status']) => {
    try {
        await api.patch(`/orders/${orderId}/status`, { status });
        handleSuccess('Order status updated successfully');
        fetchData();
    } catch (error) {
        console.error('Error updating order status:', error);
        handleError('Failed to update order status');
    }
};

  const handleDeleteOrder = async (orderId: number) => {
    if (!window.confirm('Are you sure you want to delete this order?')) {
        return;
    }

    try {
        await api.delete(`/orders/${orderId}`);
        handleSuccess('Order deleted successfully');
        fetchData();
    } catch (error) {
        console.error('Error deleting order:', error);
        handleError('Failed to delete order');
    }
};

  const handleCreateMenuItem = async () => {
    if (!newMenuItem.name || !newMenuItem.price || !newMenuItem.category_id) {
      handleError('Please fill in all required fields');
      return;
    }

    try {
      await api.post('/menu/items', newMenuItem);
      handleSuccess('Menu item created successfully');
      setShowCreateMenuItem(false);
      setNewMenuItem({
        name: '',
        description: '',
        price: 0,
        category_id: 0,
        image_url: '',
        is_available: true
      });
      fetchData();
    } catch (error) {
      console.error('Error creating menu item:', error);
      handleError('Failed to create menu item');
    }
  };

  const handleUpdateMenuItem = async (itemId: number, itemData: Partial<MenuItem>) => {
    try {
      await api.put(`/menu/items/${itemId}`, itemData);
      handleSuccess('Menu item updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating menu item:', error);
      handleError('Failed to update menu item');
    }
  };

  const handleDeleteMenuItem = async (itemId: number) => {
    if (!window.confirm('Are you sure you want to delete this menu item?')) {
      return;
    }

    try {
      await api.delete(`/menu/items/${itemId}`);
      handleSuccess('Menu item deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      handleError('Failed to delete menu item');
    }
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('Are you sure you want to archive this user?')) {
        return;
    }

    try {
        await api.delete(`/users/${userId}`);
        // Refresh the user list
        const response = await api.get('/users');
        setUsers(response.data);
        setSuccessMessage('User archived successfully');
    } catch (error) {
        console.error('Error archiving user:', error);
        setErrorMessage('Failed to archive user');
    }
  };

  const handleExportUsers = async (exportFormat: 'csv' | 'json') => {
    try {
      const response = await fetch(`${config.apiUrl}/users/export?format=${exportFormat}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (exportFormat === 'csv') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${formatDate(new Date(), 'yyyyMMdd_HHmmss')}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const data = await response.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `users_export_${formatDate(new Date(), 'yyyyMMdd_HHmmss')}.${exportFormat}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error('Error exporting users:', error);
      setError('Failed to export users');
    }
  };

  const handleDateRangeChange = (field: 'start' | 'end', value: string) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  // Menu Management Handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name) {
      alert('Please enter a category name');
      return;
    }

    try {
      await menuApi.createCategory(newCategory);
      // Refresh the categories list
      const updatedCategories = await menuApi.getCategories();
      setCategories(updatedCategories);
      setShowCreateCategory(false);
      setNewCategory({ name: '', description: '' });
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    }
  };

  const handleUpdateCategory = async (categoryId: number, categoryData: { name: string; description?: string }) => {
    try {
      await api.put(`/menu/categories/${categoryId}`, categoryData);
      const updatedCategories = categories.map(cat =>
        cat.id === categoryId ? { ...cat, ...categoryData } : cat
      );
      setCategories(updatedCategories);
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  const handleToggleItemAvailability = async (itemId: number, isAvailable: boolean) => {
    try {
      await api.put(`/menu/items/${itemId}`, { is_available: isAvailable });
      setMenuItems(menuItems.map(item => 
        item.id === itemId ? { ...item, is_available: isAvailable } : item
      ));
    } catch (error) {
      console.error('Error toggling item availability:', error);
      alert('Failed to toggle item availability');
    }
  };

  // Order Management Handlers
  const handleCreateOrderClick = async () => {
    try {
      // Validate table number
      if (!newOrder.table_number || newOrder.table_number === 0) {
        setError('Please select a table');
        return;
      }

      // Validate items
      if (newOrder.items.length === 0) {
        setError('Please add at least one item');
        return;
      }

      // Validate each item
      const invalidItems = newOrder.items.filter(item => !item.menu_item_id || item.quantity < 1);
      if (invalidItems.length > 0) {
        setError('Please select valid items and quantities');
        return;
      }

      // Calculate total amount
      const totalAmount = newOrder.items.reduce((total, item) => {
        const menuItem = menuItems.find(mi => mi.id === item.menu_item_id);
        return total + (menuItem?.price || 0) * item.quantity;
      }, 0);

      const orderData = {
        table_number: newOrder.table_number,
        items: newOrder.items,
        total_amount: totalAmount
      };

      const response = await fetch(`${config.apiUrl}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create order');
      }

      // Reset form and close modal
      setNewOrder({ table_number: 0, items: [] });
      setShowCreateOrder(false);
      setError(null);

      // Refresh orders list
      fetchOrders();
    } catch (error) {
      console.error('Error creating order:', error);
      setError(error instanceof Error ? error.message : 'Failed to create order');
    }
  };

  // Update order status comparison
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500';
      case 'preparing': return 'bg-blue-500';
      case 'ready': return 'bg-green-500';
      case 'served': return 'bg-purple-500';
      case 'cancelled': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Update order status comparison in the JSX
  const renderOrderStatus = (status: Order['status']) => {
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(status)}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Update order status button handler
  const handleStatusChange = (orderId: number, newStatus: Order['status']) => {
    handleUpdateOrderStatus(orderId, newStatus);
  };

  // Update order status buttons in the JSX
  const renderOrderActions = (order: Order) => {
    const userRole = JSON.parse(localStorage.getItem('user') || '{}').role;

    // Chef role actions
    if (userRole === 'chef') {
        switch (order.status) {
            case 'pending':
                return (
                    <button
                        key="start-preparing"
                        onClick={() => handleStatusChange(order.id, 'preparing')}
                        className="text-blue-600 hover:text-blue-900"
                    >
                        Start Preparing
                    </button>
                );
            case 'preparing':
                return (
                    <button
                        key="mark-ready"
                        onClick={() => handleStatusChange(order.id, 'ready')}
                        className="text-green-600 hover:text-green-900"
                    >
                        Mark Ready
                    </button>
                );
            default:
                return null;
        }
    }

    // Server role actions
    if (userRole === 'server') {
        if (order.status === 'ready') {
            return (
                <button
                    key="mark-served"
                    onClick={() => handleStatusChange(order.id, 'served')}
                    className="text-purple-600 hover:text-purple-900"
                >
                    Mark Served
                </button>
            );
        }
        return null;
    }

    // Admin role actions (default)
    switch (order.status) {
      case 'pending':
        return (
          <>
            <button
              key="start-preparing"
              onClick={() => handleStatusChange(order.id, 'preparing')}
              className="text-blue-600 hover:text-blue-900"
            >
              Start Preparing
            </button>
            <button
              key="cancel"
              onClick={() => handleStatusChange(order.id, 'cancelled')}
              className="text-red-600 hover:text-red-900"
            >
              Cancel
            </button>
          </>
        );
      case 'preparing':
        return (
          <>
            <button
              key="mark-ready"
              onClick={() => handleStatusChange(order.id, 'ready')}
              className="text-green-600 hover:text-green-900"
            >
              Mark Ready
            </button>
            <button
              key="cancel"
              onClick={() => handleStatusChange(order.id, 'cancelled')}
              className="text-red-600 hover:text-red-900"
            >
              Cancel
            </button>
          </>
        );
      case 'ready':
        return (
          <button
            key="mark-served"
            onClick={() => handleStatusChange(order.id, 'served')}
            className="text-purple-600 hover:text-purple-900"
          >
            Mark Served
          </button>
        );
      default:
        return null;
    }
  };

  // Update the render functions to show loading states
  const renderLoadingState = (section: string) => (
    <div className="flex justify-center items-center p-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <span className="ml-2 text-gray-600">Loading {section}...</span>
    </div>
  );

  const renderErrorState = () => (
    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
      <strong className="font-bold">Error: </strong>
      <span className="block sm:inline">{error}</span>
      <button
        onClick={() => setError(null)}
        className="absolute top-0 bottom-0 right-0 px-4 py-3"
      >
        <span className="sr-only">Dismiss</span>
        <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
          <title>Close</title>
          <path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/>
        </svg>
      </button>
    </div>
  );

  const renderNavigation = () => {
    if (userRole === 'admin') {
      return (
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded ${activeTab === 'users' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('menu')}
            className={`px-4 py-2 rounded ${activeTab === 'menu' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Menu Management
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded ${activeTab === 'orders' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab('tables')}
            className={`px-4 py-2 rounded ${activeTab === 'tables' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Tables
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            className={`px-4 py-2 rounded ${activeTab === 'sales' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Sales
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded ${activeTab === 'orders' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Orders
          </button>
          <button
            onClick={() => setActiveTab('tables')}
            className={`px-4 py-2 rounded ${activeTab === 'tables' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
          >
            Tables
          </button>
        </div>
      );
    }
  };

  const renderUsersSection = () => {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <ActionButton
              onClick={() => setShowCreateUser(true)}
              variant="primary"
              className="w-full sm:w-auto"
            >
              Create User
            </ActionButton>
            <ActionButton
              onClick={() => setShowBulkActions(true)}
              variant="secondary"
              className="w-full sm:w-auto"
            >
              Bulk Actions
            </ActionButton>
          </div>
        </div>

        {isLoading.users ? (
          renderLoadingState('users')
        ) : error ? (
          renderErrorState()
        ) : (
          <div className="bg-white shadow rounded-lg">
            <TableView
              data={users}
              columns={[
                {
                  header: 'Username',
                  key: 'username',
                  render: (user: User) => (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUsers([...selectedUsers, user.id]);
                          } else {
                            setSelectedUsers(selectedUsers.filter(id => id !== user.id));
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                      />
                      <span>{user.username}</span>
                    </div>
                  )
                },
                {
                  header: 'Role',
                  key: 'role',
                  render: (user: User) => (
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                      {user.role}
                    </span>
                  )
                },
                {
                  header: 'Status',
                  key: 'is_active',
                  render: (user: User) => (
                    <select
                      value={user.is_active ? 'active' : 'inactive'}
                      onChange={(e) => handleUpdateStatus(user.id, e.target.value as 'active' | 'inactive' | 'archived')}
                      className={`px-2 py-1 text-xs rounded-full ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      } border-0 focus:ring-2 focus:ring-blue-500`}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archive</option>
                    </select>
                  )
                },
                {
                  header: 'Last Login',
                  key: 'last_login',
                  render: (user: User) => (
                    user.last_login ? formatDate(new Date(user.last_login), 'yyyy-MM-dd HH:mm:ss') : 'Never'
                  )
                },
                {
                  header: 'Actions',
                  key: 'actions',
                  render: (user: User) => (
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                      <ActionButton
                        onClick={() => handleResetPin(user.id)}
                        variant="primary"
                        className="text-sm"
                      >
                        Change PIN
                      </ActionButton>
                      <ActionButton
                        onClick={() => {
                          setSelectedUserForRoleChange(user.id);
                          setNewRole(user.role);
                          setShowRoleModal(true);
                        }}
                        variant="secondary"
                        className="text-sm"
                      >
                        Change Role
                      </ActionButton>
                      <ActionButton
                        onClick={() => handleDeleteUser(user.id)}
                        variant="danger"
                        className="text-sm"
                      >
                        Delete
                      </ActionButton>
                    </div>
                  )
                }
              ]}
              isMobile={isMobile}
            />
          </div>
        )}

        <Modal
          isOpen={showCreateUser}
          onClose={() => {
            setShowCreateUser(false);
            setNewUser({ username: '', pin: '', role: '' });
            setError(null);
          }}
          title="Create New User"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Username</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">PIN</label>
              <input
                type="password"
                value={newUser.pin}
                onChange={(e) => setNewUser({ ...newUser, pin: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <select
                value={newUser.role}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoadingRoles}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </select>
              {isLoadingRoles && (
                <p className="mt-1 text-sm text-gray-500">Loading roles...</p>
              )}
            </div>
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            <div className="flex justify-end space-x-3">
              <ActionButton
                onClick={() => {
                  setShowCreateUser(false);
                  setNewUser({ username: '', pin: '', role: '' });
                  setError(null);
                }}
                variant="secondary"
              >
                Cancel
              </ActionButton>
              <ActionButton
                onClick={() => handleCreateUser(newUser)}
                variant="primary"
                disabled={isLoadingRoles}
              >
                Create
              </ActionButton>
            </div>
          </div>
        </Modal>

        {/* Bulk Actions Modal */}
        <Modal
          isOpen={showBulkActions}
          onClose={() => {
            setShowBulkActions(false);
            setBulkAction('');
            setError(null);
          }}
          title="Bulk Actions"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Action</label>
              <select
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select an action</option>
                <option value="delete">Delete Selected Users</option>
                <option value="activate">Activate Selected Users</option>
                <option value="deactivate">Deactivate Selected Users</option>
              </select>
            </div>
            {error && (
              <div className="text-red-600 text-sm">{error}</div>
            )}
            <div className="flex justify-end space-x-3">
              <ActionButton
                onClick={() => {
                  setShowBulkActions(false);
                  setBulkAction('');
                  setError(null);
                }}
                variant="secondary"
              >
                Cancel
              </ActionButton>
              <ActionButton
                onClick={handleBulkAction}
                variant="primary"
                disabled={!bulkAction || selectedUsers.length === 0}
              >
                Apply
              </ActionButton>
            </div>
          </div>
        </Modal>

        {/* Role Change Modal */}
        <Modal
          isOpen={showRoleModal}
          onClose={() => {
            setShowRoleModal(false);
            setSelectedUserForRoleChange(null);
            setNewRole('');
          }}
          title="Change User Role"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">New Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                disabled={isLoadingRoles}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name} - {role.description}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <ActionButton
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUserForRoleChange(null);
                  setNewRole('');
                }}
                variant="secondary"
              >
                Cancel
              </ActionButton>
              <ActionButton
                onClick={() => selectedUserForRoleChange && handleUpdateRole(selectedUserForRoleChange)}
                variant="primary"
                disabled={!newRole}
              >
                Update Role
              </ActionButton>
            </div>
          </div>
        </Modal>
      </div>
    );
  };

  const renderSection = () => {
    switch (activeTab) {
      case 'users':
        return renderUsersSection();
      case 'orders':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Orders</h2>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowArchived(!showArchived)}
                  className={`px-4 py-2 rounded-md ${
                    showArchived ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {showArchived ? 'Show Active' : 'Show Archived'}
                </button>
              </div>
            </div>

            {isLoading.orders ? (
              <div className="text-center py-4">Loading orders...</div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {orders
                        .filter(order => showArchived ? order.status === 'served' : order.status !== 'served')
                        .map((order) => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{order.id}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Table {order.table_number}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs rounded-full ${
                                order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                order.status === 'preparing' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'ready' ? 'bg-green-100 text-green-800' :
                                order.status === 'served' ? 'bg-purple-100 text-purple-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900">
                                {order.items?.map((item, index) => (
                                  <div key={index} className="flex justify-between items-center mb-1">
                                    <span>{item.menu_item_name}</span>
                                    <span className="text-gray-500">x{item.quantity}</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ${order.total_amount.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => handleViewOrderDetails(order)}
                                  className="text-blue-600 hover:text-blue-900"
                                >
                                  View Details
                                </button>
                                {order.status === 'ready' && (
                                  <button
                                    onClick={() => handleMarkServed(order.id)}
                                    className="text-green-600 hover:text-green-900"
                                  >
                                    Mark Served
                                  </button>
                                )}
                                {order.status === 'pending' && (
                                  <button
                                    onClick={() => handleCancelOrder(order.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      case 'menu':
        console.log('Rendering menu section with categories:', categories);
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">Menu Management</h2>
              <div className="space-x-2">
                <button
                  onClick={() => setShowCreateCategory(true)}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  Add Category
                </button>
                <button
                  onClick={() => setShowCreateMenuItem(true)}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                >
                  Add Menu Item
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Categories</h3>
              {isLoading.categories ? (
                renderLoadingState('categories')
              ) : categories && categories.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {categories.map((category) => (
                        <tr key={category.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{category.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{category.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500">No categories found</p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Menu Items</h3>
              {isLoading.menuItems ? (
                renderLoadingState('menu items')
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {menuItems.map((item) => (
                        <tr key={item.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {categories.find(c => c.id === item.category_id)?.name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${item.price.toFixed(2)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              item.is_available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {item.is_available ? 'Available' : 'Unavailable'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => {
                                setShowEditMenuItem(item.id);
                                setNewMenuItem({
                                  name: item.name,
                                  category_id: item.category_id,
                                  description: item.description || '',
                                  price: item.price,
                                  image_url: item.image_url || '',
                                  is_available: item.is_available
                                });
                              }}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      case 'sales':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Sales</p>
                    <h3 className="text-2xl font-bold text-gray-800">${sales.total?.toFixed(2) || '0.00'}</h3>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Today's Sales</p>
                    <h3 className="text-2xl font-bold text-gray-800">${sales.today?.toFixed(2) || '0.00'}</h3>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Transactions</h3>
              <div className="space-y-4">
                {sales.recent?.map((sale) => (
                  <div key={sale.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-gray-800">Transaction #{sale.id}</p>
                      <p className="text-sm text-gray-500">{formatDate(new Date(sale.date), 'yyyy-MM-dd HH:mm')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-800">${sale.amount?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Sales Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Total Transactions</p>
                  <p className="text-xl font-bold text-gray-800">{sales.recent?.length || 0}</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Average Transaction</p>
                  <p className="text-xl font-bold text-gray-800">
                    ${(sales.total / (sales.recent?.length || 1)).toFixed(2)}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500">Today's Transactions</p>
                  <p className="text-xl font-bold text-gray-800">
                    {sales.recent?.filter(sale => 
                      new Date(sale.date).toDateString() === new Date().toDateString()
                    ).length || 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'statistics':
        return (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">User Statistics</h2>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                    className="border rounded px-2 py-1"
                  />
                  <span>to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                    className="border rounded px-2 py-1"
                  />
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleExportUsers('csv')}
                    className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={() => handleExportUsers('json')}
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                  >
                    Export JSON
                  </button>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              {isLoading.orders ? (
                renderLoadingState('statistics')
              ) : (
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Days</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Logins</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders Modified</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {statistics.map((stat) => (
                      <tr key={stat.username}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{stat.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stat.role}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stat.active_days}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stat.total_logins}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stat.total_orders_created}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{stat.total_orders_modified}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      case 'tables':
        return (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Table Management</h2>
              <button
                onClick={() => setShowCreateTable(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Add Table
              </button>
            </div>
            {tablesLoading ? (
              <div className="text-center py-4">Loading tables...</div>
            ) : tablesError ? (
              <div className="text-red-600">{tablesError}</div>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tables.map((table) => (
                      <tr key={table.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{table.number}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            table.status === 'available' 
                              ? 'bg-green-100 text-green-800'
                              : table.status === 'occupied'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {table.status.charAt(0).toUpperCase() + table.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <button
                            onClick={() => handleDeleteTable(table.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  // Add this useEffect near the other useEffects
  useEffect(() => {
    if (showCreateMenuItem) {
      console.log('Menu item form opened. Categories:', categories);
    }
  }, [showCreateMenuItem, categories]);

  // Add function to fetch sales data
  const fetchSalesData = async () => {
    try {
      setIsLoading(prev => ({ ...prev, sales: true }));
      const salesData = await salesApi.getSales();
      console.log('Sales data received:', salesData);
      setSales(salesData);
      setError(null);
    } catch (error) {
      console.error('Error fetching sales data:', error);
      // Type check the error before accessing its properties
      if (axios.isAxiosError(error) && error.response?.status !== 404) {
        setError(error.message || 'Failed to fetch sales data');
      } else if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to fetch sales data');
      }
    } finally {
      setIsLoading(prev => ({ ...prev, sales: false }));
    }
  };

  // Add useEffect for fetching sales data
  useEffect(() => {
    if (activeTab === 'sales') {
      fetchSalesData();
    }
  }, [activeTab]);

  // Add useEffect for initial data fetch
  useEffect(() => {
    fetchTablesData();
    fetchOrders();
    fetchMenuItems();
    fetchCategories();
  }, []);

  // Add useEffect for tab-specific data fetching
  useEffect(() => {
    switch (activeTab) {
      case 'users':
        setIsLoading(prev => ({ ...prev, users: true }));
        userApi.getUsers()
          .then(data => {
            setUsers(data);
            setError(null);
          })
          .catch(error => {
            console.error('Error fetching users:', error);
            if (axios.isAxiosError(error)) {
              setError(error.message || 'Failed to fetch users');
            } else if (error instanceof Error) {
              setError(error.message);
            } else {
              setError('Failed to fetch users');
            }
          })
          .finally(() => {
            setIsLoading(prev => ({ ...prev, users: false }));
          });
        break;

      case 'menu':
        setIsLoading(prev => ({ ...prev, categories: true, menuItems: true }));
        Promise.all([
          menuApi.getCategories(),
          menuApi.getMenuItems()
        ])
          .then(([categoriesData, menuItemsData]) => {
            setCategories(categoriesData);
            setMenuItems(menuItemsData);
            setError(null);
          })
          .catch(error => {
            console.error('Error fetching menu data:', error);
            if (axios.isAxiosError(error)) {
              setError(error.message || 'Failed to fetch menu data');
            } else if (error instanceof Error) {
              setError(error.message);
            } else {
              setError('Failed to fetch menu data');
            }
          })
          .finally(() => {
            setIsLoading(prev => ({ ...prev, categories: false, menuItems: false }));
          });
        break;

      case 'orders':
        fetchOrders();
        break;

      case 'sales':
        fetchSalesData();
        break;
    }
  }, [activeTab]);

  // Add useEffect for fetching tables
  useEffect(() => {
    if (activeTab === 'tables') {
      fetchTablesData();
    }
  }, [activeTab]);

  // Update confirmDeleteTable function
  const confirmDeleteTable = async () => {
    if (!tableToDelete) return;

    try {
      await dispatch(deleteTable(tableToDelete));
      fetchTablesData();
    } catch (error) {
      console.error('Error deleting table:', error);
      setError('Failed to delete table');
    } finally {
      setShowDeleteTableConfirm(false);
      setTableToDelete(null);
    }
  };

  // Add useIsMobile hook inside the component
  const useIsMobile = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    
    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    return isMobile;
  };

  const isMobile = useIsMobile();

  const fetchMenuItems = async () => {
    setIsLoading(prev => ({ ...prev, menuItems: true }));
    try {
      const response = await menuApi.getMenuItems();
      setMenuItems(response);
    } catch (error) {
      console.error('Error fetching menu items:', error);
      setError('Failed to fetch menu items');
    } finally {
      setIsLoading(prev => ({ ...prev, menuItems: false }));
    }
  };

  const fetchCategories = async () => {
    setIsLoading(prev => ({ ...prev, categories: true }));
    try {
      const response = await menuApi.getCategories();
      setCategories(response);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setError('Failed to fetch categories');
    } finally {
      setIsLoading(prev => ({ ...prev, categories: false }));
    }
  };

  const fetchOrdersForTable = async (tableId: number) => {
    try {
      const response = await api.get('/orders');
      const tableOrders = response.data.filter((order: OrderType) => order.table_number === tableId);
      setSelectedTableOrders(tableOrders);
    } catch (error) {
      console.error('Error fetching table orders:', error);
      setError('Failed to fetch table orders');
    }
  };

  // Add this function to handle restoring archived users
  const handleRestoreUser = async (userId: number) => {
    try {
        await api.patch(`/users/${userId}/restore`);
        // Refresh the user list
        const response = await api.get('/users');
        setUsers(response.data);
        setSuccessMessage('User restored successfully');
    } catch (error) {
        console.error('Error restoring user:', error);
        setErrorMessage('Failed to restore user');
    }
  };

  // Add this function to handle status changes
  const handleUpdateStatus = async (userId: number, newStatus: 'active' | 'inactive' | 'archived') => {
    try {
        if (newStatus === 'archived') {
            await api.delete(`/users/${userId}`);
        } else {
            await api.patch(`/users/${userId}/status`, { status: newStatus });
        }
        // Refresh the user list
        const response = await api.get('/users');
        setUsers(response.data);
        setSuccessMessage(`User status updated to ${newStatus}`);
    } catch (error) {
        console.error('Error updating user status:', error);
        setErrorMessage('Failed to update user status');
    }
};

  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Function to add a toast
  const addToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    // Remove toast after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  // Update the message handlers to use toasts
  const handleSuccess = (message: string) => {
    setSuccessMessage(message);
    addToast(message, 'success');
  };

  const handleError = (message: string) => {
    setErrorMessage(message);
    addToast(message, 'error');
  };

  // Add this new handler for the create user button
  const handleCreateUserClick = () => {
    setShowCreateUser(true);
  };

  // Add fetchRoles function
  const fetchRoles = async () => {
    try {
      setIsLoadingRoles(true);
      const response = await api.get('/roles');
      setRoles(response.data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      handleError('Failed to fetch roles');
    } finally {
      setIsLoadingRoles(false);
    }
  };

  // Update useEffect to fetch roles
  useEffect(() => {
    fetchData();
    fetchRoles();
  }, []);

  // Add useEffect to set user role on component mount
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('Dashboard mounted, user from localStorage:', user);
    console.log('Dashboard mounted, user role:', user?.role);
    console.log('Dashboard mounted, available role titles:', roleTitles);
    console.log('Dashboard mounted, role title lookup:', roleTitles[user?.role]);
    setUserRole(user.role || '');
  }, []);

  const getDashboardTitle = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('getDashboardTitle - User from localStorage:', user);
    console.log('getDashboardTitle - User role:', user?.role);
    console.log('getDashboardTitle - Available role titles:', roleTitles);
    console.log('getDashboardTitle - Role title lookup:', roleTitles[user?.role]);
    const title = roleTitles[user?.role] || 'Dashboard';
    console.log('getDashboardTitle - Selected title:', title);
    return title;
  };

  const getDashboardSubtitle = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    console.log('getDashboardSubtitle - User from localStorage:', user);
    console.log('getDashboardSubtitle - User role:', user?.role);
    const subtitle = roleSubtitles[user?.role] || 'Welcome back!';
    console.log('getDashboardSubtitle - Selected subtitle:', subtitle);
    return subtitle;
  };

  // Add logging before render
  console.log('Dashboard render - Current user role:', userRole);
  console.log('Dashboard render - Title:', getDashboardTitle());
  console.log('Dashboard render - Subtitle:', getDashboardSubtitle());

  const handleViewOrderDetails = (order: OrderType) => {
    setSelectedOrder(order);
    setShowOrderDetails(true);
  };

  const renderOrdersSection = () => {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Orders</h2>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`px-4 py-2 rounded-md ${
                showArchived ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              {showArchived ? 'Show Active' : 'Show Archived'}
            </button>
          </div>
        </div>

        {isLoading.orders ? (
          <div className="text-center py-4">Loading orders...</div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders
                    .filter(order => showArchived ? order.status === 'served' : order.status !== 'served')
                    .map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{order.id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">Table {order.table_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            order.status === 'preparing' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'ready' ? 'bg-green-100 text-green-800' :
                            order.status === 'served' ? 'bg-purple-100 text-purple-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {order.items?.map((item, index) => (
                              <div key={index} className="flex justify-between items-center mb-1">
                                <span>{item.menu_item_name}</span>
                                <span className="text-gray-500">x{item.quantity}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${order.total_amount.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewOrderDetails(order)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View Details
                            </button>
                            {order.status === 'ready' && (
                              <button
                                onClick={() => handleMarkServed(order.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                Mark Served
                              </button>
                            )}
                            {order.status === 'pending' && (
                              <button
                                onClick={() => handleCancelOrder(order.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Add Order Details Modal
  const renderOrderDetailsModal = () => {
    if (!selectedOrder || !showOrderDetails) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Order #{selectedOrder.id} Details</h3>
            <button
              onClick={() => setShowOrderDetails(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500">Table</h4>
              <p className="mt-1 text-sm text-gray-900">Table {selectedOrder.table_number}</p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500">Status</h4>
              <p className="mt-1 text-sm text-gray-900">
                {selectedOrder.status.charAt(0).toUpperCase() + selectedOrder.status.slice(1)}
              </p>
            </div>

    <div>
  <h4 className="text-sm font-medium text-gray-500">Items</h4>
  <div className="mt-2 space-y-2">
    
    {/* Header Row */}
    <div className="flex justify-between items-center font-semibold text-gray-700 border-b pb-2">
      <span>Item</span>
      <span className="text-right">Qty / Price / Total</span>
    </div>

    {/* Order Items */}
    {selectedOrder.items?.map((item) => (
      <div key={item.id} className="flex justify-between border-b pb-4">
        
        {/* Left: Item name and notes */}
        <div className="w-1/2 pr-2">
          <h3 className="text-base font-medium text-gray-900">{item.item_name}</h3>
          {item.notes && (
            <p className="text-sm text-gray-500 mt-1">📝 {item.notes}</p>
          )}
        </div>
        
        {/* Right: Qty / Price / Total */}
        <div className="w-1/2 text-right">
          <p className="text-sm text-gray-500">🔢 Qty: {item.quantity}</p>
          <p className="text-sm text-gray-500">💲Each: ${item.price_at_time.toFixed(2)}</p>
          <p className="text-base font-bold text-blue-600 mt-1">
            💰 ${(item.price_at_time * item.quantity).toFixed(2)}
          </p>
        </div>
      </div>
    ))}
  </div>
</div>


            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">Total</span>
                <span className="text-lg font-semibold text-gray-900">
                  ${selectedOrder.total_amount.toFixed(2)}
                </span>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowOrderDetails(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              {selectedOrder.status === 'ready' && (
                <button
                  onClick={() => {
                    handleMarkServed(selectedOrder.id);
                    setShowOrderDetails(false);
                  }}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Mark as Served
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleMarkServed = async (orderId: number) => {
    try {
      await orderApi.updateOrderStatus(orderId, 'served');
      fetchOrders();
      dispatch(fetchTables());
      addToast('Order marked as served', 'success');
    } catch (error) {
      console.error('Error marking order as served:', error);
      addToast('Failed to mark order as served', 'error');
    }
  };

  const handleCancelOrder = async (orderId: number) => {
    try {
      await orderApi.updateOrderStatus(orderId, 'cancelled');
      fetchOrders();
      dispatch(fetchTables());
      addToast('Order cancelled successfully', 'success');
    } catch (error) {
      console.error('Error cancelling order:', error);
      addToast('Failed to cancel order', 'error');
    }
  };

  // Add the modal to the main render
  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {getDashboardTitle()}
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            {getDashboardSubtitle()}
          </p>
        </div>
        
      {/* Toast Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
          {toasts.map(toast => (
              <div
                  key={toast.id}
                  className={`px-4 py-2 rounded-lg shadow-lg transform transition-all duration-300 ${
                      toast.type === 'success' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-red-500 text-white'
                  }`}
              >
                  {toast.message}
              </div>
          ))}
      </div>

        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handleLogout}
              className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
            >
              Logout
            </button>
          </div>

          {/* Role-based navigation */}
          <div className="flex space-x-4 mb-6">
            {userRole === 'admin' ? (
              <>
                <button
                  onClick={() => setActiveTab('users')}
                  className={`px-4 py-2 rounded ${activeTab === 'users' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  User Management
                </button>
                <button
                  onClick={() => setActiveTab('menu')}
                  className={`px-4 py-2 rounded ${activeTab === 'menu' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Menu Management
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`px-4 py-2 rounded ${activeTab === 'orders' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Orders
                </button>
                <button
                  onClick={() => setActiveTab('tables')}
                  className={`px-4 py-2 rounded ${activeTab === 'tables' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Tables
                </button>
                <button
                  onClick={() => setActiveTab('sales')}
                  className={`px-4 py-2 rounded ${activeTab === 'sales' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Sales
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`px-4 py-2 rounded ${activeTab === 'orders' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Orders
                </button>
                <button
                  onClick={() => setActiveTab('tables')}
                  className={`px-4 py-2 rounded ${activeTab === 'tables' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Tables
                </button>
              </>
            )}
          </div>

          {error && renderErrorState()}
          {renderSection()}

          {/* Create Order Modal */}
          {showCreateOrder && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Create New Order</h2>
                  <button
                    onClick={() => setShowCreateOrder(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Table Number</label>
                    <select
                      value={newOrder.table_number}
                      onChange={(e) => setNewOrder({ ...newOrder, table_number: Number(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select a table</option>
                      {tables
                        .filter(table => table.status === 'available')
                        .map(table => (
                          <option key={table.id} value={table.number}>
                            Table {table.number}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Menu Items</label>
                    <div className="space-y-2">
                      {newOrder.items.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <select
                            value={item.menu_item_id}
                            onChange={(e) => {
                              const updatedItems = [...newOrder.items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                menu_item_id: Number(e.target.value)
                              };
                              setNewOrder({ ...newOrder, items: updatedItems });
                            }}
                            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            <option value="">Select an item</option>
                            {menuItems
                              .filter(item => item.is_available)
                              .map(menuItem => (
                                <option key={menuItem.id} value={menuItem.id}>
                                  {menuItem.name} - ${menuItem.price.toFixed(2)}
                                </option>
                              ))}
                          </select>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const updatedItems = [...newOrder.items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                quantity: Number(e.target.value)
                              };
                              setNewOrder({ ...newOrder, items: updatedItems });
                            }}
                            className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                          <button
                            onClick={() => {
                              const updatedItems = newOrder.items.filter((_, i) => i !== index);
                              setNewOrder({ ...newOrder, items: updatedItems });
                            }}
                            className="text-red-600 hover:text-red-900"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          setNewOrder({
                            ...newOrder,
                            items: [...newOrder.items, { menu_item_id: 0, quantity: 1 }]
                          });
                        }}
                        className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                      >
                        + Add Item
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => setShowCreateOrder(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateOrderClick}
                      className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                      Create Order
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Delete Table Confirmation Modal */}
          {showDeleteTableConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
                <p className="text-gray-600 mb-6">Are you sure you want to delete this table?</p>
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => {
                      setShowDeleteTableConfirm(false);
                      setShowDeleteConfirm(null);
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteTable}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Create Table Modal */}
          {showCreateTable && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Create New Table</h2>
                  <button
                    onClick={() => {
                      setShowCreateTable(false);
                      setNewTableNumber('');
                      setError(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Table Number</label>
                    <input
                      type="number"
                      value={newTableNumber}
                      onChange={(e) => setNewTableNumber(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      min="1"
                      placeholder="Enter table number"
                    />
                  </div>
                  {error && (
                    <div className="text-red-600 text-sm">{error}</div>
                  )}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowCreateTable(false);
                        setNewTableNumber('');
                        setError(null);
                      }}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateTable}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Category Modal */}
          {showCreateCategory && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Create New Category</h2>
                  <button
                    onClick={() => {
                      setShowCreateCategory(false);
                      setNewCategory({ name: '', description: '' });
                      setError(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category Name</label>
                    <input
                      type="text"
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter category name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter category description"
                      rows={3}
                    />
                  </div>
                  {error && (
                    <div className="text-red-600 text-sm">{error}</div>
                  )}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowCreateCategory(false);
                        setNewCategory({ name: '', description: '' });
                        setError(null);
                      }}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateCategory}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Menu Item Modal */}
          {showCreateMenuItem && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-lg p-6 max-w-sm w-full">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-semibold text-gray-800">Create New Menu Item</h2>
                  <button
                    onClick={() => {
                      setShowCreateMenuItem(false);
                      setNewMenuItem({
                        name: '',
                        description: '',
                        price: 0,
                        category_id: 0,
                        image_url: '',
                        is_available: true
                      });
                      setError(null);
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      value={newMenuItem.name}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter item name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                      value={newMenuItem.description}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter item description"
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Price</label>
                    <input
                      type="number"
                      value={newMenuItem.price}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, price: Number(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter price"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Category</label>
                    <select
                      value={newMenuItem.category_id}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, category_id: Number(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Image URL</label>
                    <input
                      type="text"
                      value={newMenuItem.image_url}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, image_url: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Enter image URL"
                    />
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={newMenuItem.is_available}
                      onChange={(e) => setNewMenuItem({ ...newMenuItem, is_available: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">Available</label>
                  </div>
                  {error && (
                    <div className="text-red-600 text-sm">{error}</div>
                  )}
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => {
                        setShowCreateMenuItem(false);
                        setNewMenuItem({
                          name: '',
                          description: '',
                          price: 0,
                          category_id: 0,
                          image_url: '',
                          is_available: true
                        });
                        setError(null);
                      }}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateMenuItem}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                    >
                      Create
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Menu Item Modal */}
          {showEditMenuItem !== null && (
            <Modal
              isOpen={showEditMenuItem !== null}
              onClose={() => setShowEditMenuItem(null)}
              title="Edit Menu Item"
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    value={newMenuItem.name}
                    onChange={e => setNewMenuItem({ ...newMenuItem, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={newMenuItem.description}
                    onChange={e => setNewMenuItem({ ...newMenuItem, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Price</label>
                  <input
                    type="number"
                    value={newMenuItem.price}
                    onChange={e => setNewMenuItem({ ...newMenuItem, price: Number(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={newMenuItem.category_id}
                    onChange={e => setNewMenuItem({ ...newMenuItem, category_id: Number(e.target.value) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Image URL</label>
                  <input
                    type="text"
                    value={newMenuItem.image_url}
                    onChange={e => setNewMenuItem({ ...newMenuItem, image_url: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newMenuItem.is_available}
                    onChange={e => setNewMenuItem({ ...newMenuItem, is_available: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900">Available</label>
                </div>
                {error && (
                  <div className="text-red-600 text-sm">{error}</div>
                )}
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowEditMenuItem(null)}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleUpdateMenuItem(showEditMenuItem, newMenuItem);
                      setShowEditMenuItem(null);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Modal>
          )}
        </div>
      </div>
      {/* Add the order details modal */}
      {renderOrderDetailsModal()}
    </div>
  );
};

export default Dashboard; 